import { useEffect, useMemo } from 'react';
import {
  ControllerRenderProps,
  useController,
  useFormContext,
} from 'react-hook-form';
import { UseQueryResult } from '@tanstack/react-query';
import { ClientInputFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import useGetBookingTourOptions from '@/features/public/booking/hooks/useGetBookingTourOptions.ts';
import useGetWaitlistTourOptions from '@/features/dashboard/waitlist/hooks/useGetWaitlistTourOptions.ts';
import PrimitiveSingleSelect from '@/components/form/_primitives/PrimitiveSingleSelect.tsx';
import PrimitiveMultiSelect from '@/components/form/_primitives/PrimitiveMultiSelect.tsx';
import {
  BookingOptionDto,
  BookingSelectedOptionDto,
} from '@/features/public/booking/types/BookingOptionDto.ts';
import { CardTitle, Card, CardContent } from '@/components/ui/Card.tsx';
import {
  getPricesDisplayString,
  sumPricesDisplayString,
} from '@/features/shared/pricing/util/PricingFormatter.ts';
import InputError from '@/components/form/base/InputError.tsx';
import Option from '@/components/form/types/Option.ts';
import Chip from '@/components/ui/Chip.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import TourOptionsFormSkeleton from '@/features/public/booking/components/TourOptionsFormSkeleton.tsx';
import { BookingScope } from '@/features/shared/booking/types/BookingScope.ts';
import { SelectedOptionEntry } from '@/features/public/booking/schemas/participantTourOptionsSchema.ts';

interface TourOptionsProps {
  index: number;
  scope: BookingScope;
  /** Komunikaty z wyceny per opcja — pusta mapa poza publicznym formularzem. */
  optionErrors?: Record<string, string[]>;
}

const TourOptions = ({ index, scope, optionErrors }: TourOptionsProps) => {
  if (scope.type === 'public') {
    return (
      <PublicTourOptionsInternal
        index={index}
        orgSlug={scope.orgSlug}
        tourSlug={scope.tourSlug}
        optionErrors={optionErrors}
      />
    );
  } else {
    return (
      <DashboardTourOptionsInternal
        index={index}
        tourId={scope.tourId}
        optionErrors={optionErrors}
      />
    );
  }
};

const PublicTourOptionsInternal = ({
  index,
  orgSlug,
  tourSlug,
  optionErrors,
}: {
  index: number;
  orgSlug: string;
  tourSlug: string;
  optionErrors?: Record<string, string[]>;
}) => {
  const { watch } = useFormContext<ClientInputFormWithCustomFields>();
  const birthDate = watch(`participants.${index}.birthDate`);
  const query = useGetBookingTourOptions(orgSlug, tourSlug, birthDate);

  return (
    <TourOptionsDisplay
      index={index}
      query={query}
      optionErrors={optionErrors}
    />
  );
};

const DashboardTourOptionsInternal = ({
  index,
  tourId,
  optionErrors,
}: {
  index: number;
  tourId: string;
  optionErrors?: Record<string, string[]>;
}) => {
  const { watch } = useFormContext<ClientInputFormWithCustomFields>();
  const birthDate = watch(`participants.${index}.birthDate`);
  const query = useGetWaitlistTourOptions(tourId, birthDate);

  return (
    <TourOptionsDisplay
      index={index}
      query={query}
      optionErrors={optionErrors}
    />
  );
};

interface TourOptionsDisplayProps {
  index: number;
  query: UseQueryResult<BookingOptionDto[], Error>;
  optionErrors?: Record<string, string[]>;
}

const TourOptionsDisplay = ({
  index,
  query,
  optionErrors,
}: TourOptionsDisplayProps) => {
  const { setValue, getValues } =
    useFormContext<ClientInputFormWithCustomFields>();
  const { data: bookingOptions, dataUpdatedAt, isLoading, isError } = query;

  useEffect(() => {
    if (!bookingOptions || bookingOptions.length === 0) return;

    const current =
      getValues(`participants.${index}.selectedOptions`) ??
      ({} as Record<string, SelectedOptionEntry>);

    // Lista opcji przychodzi na nowo także przy powrocie do karty przeglądarki
    // (staleTime 0 + refetchOnWindowFocus), więc czyszczenie wszystkiego
    // kasowało klientowi wybory bez powodu. Znika tylko to, czego backend już
    // nie oferuje — a że próg cenowy jest dobierany do wieku, zmiana daty
    // urodzenia sama unieważnia wybrane warianty.
    const refreshed = Object.fromEntries(
      bookingOptions.map((option) => [
        option.optionId,
        {
          optionDto: option,
          selections: (current[option.optionId]?.selections ?? []).filter(
            (selection) =>
              option.variants.some(
                (variant) =>
                  variant.variantId === selection.variantId &&
                  variant.priceId === selection.priceId,
              ),
          ),
        },
      ]),
    );

    setValue(`participants.${index}.selectedOptions`, refreshed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, index]);

  if (isLoading) {
    return <TourOptionsFormSkeleton />;
  }

  if (isError) {
    return <LoadError subject="opcji rezerwacji" />;
  }

  if (!bookingOptions || bookingOptions.length === 0) {
    return null;
  }

  const requiredOptions = bookingOptions.filter((option) => option.required);
  const notRequiredOptions = bookingOptions.filter(
    (option) => !option.required,
  );

  return (
    <div className="space-y-4">
      {requiredOptions.length > 0 && (
        <>
          <CardTitle className="pt-2">Opcje obowiązkowe</CardTitle>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {requiredOptions.map((option) => (
              <TourOption
                key={option.optionId}
                option={option}
                participantIndex={index}
                quoteErrors={optionErrors?.[option.optionId]}
              />
            ))}
          </div>
        </>
      )}

      {notRequiredOptions.length > 0 && (
        <>
          <CardTitle className="pt-2">Opcje fakultatywne</CardTitle>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {notRequiredOptions.map((option) => (
              <TourOption
                key={option.optionId}
                option={option}
                participantIndex={index}
                quoteErrors={optionErrors?.[option.optionId]}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const TourOption = ({
  option,
  participantIndex,
  quoteErrors,
}: {
  option: BookingOptionDto;
  participantIndex: number;
  quoteErrors?: string[];
}) => {
  const form = useFormContext<ClientInputFormWithCustomFields>();

  const variantOptions = option.variants.map((variant) => ({
    label: `${variant.name} (${variant.price.amount} ${variant.price.currency})`,
    value: variant,
  }));

  const { field } = useController({
    name: `participants.${participantIndex}.selectedOptions.${option.optionId}.selections`,
    control: form.control,
  });

  const optionSelections = form.watch(
    `participants.${participantIndex}.selectedOptions.${option.optionId}.selections`,
  );

  const selectedPrices = useMemo(() => {
    if (!optionSelections || optionSelections.length === 0) {
      return undefined;
    }
    const variantIds = optionSelections.map((s) => s.variantId);
    return option.variants
      .filter((v) => variantIds.includes(v.variantId))
      .map((v) => v.price);
  }, [optionSelections, option]);

  return (
    <Card>
      <CardContent className="flex h-full flex-col justify-between space-y-2 p-3">
        <div className="space-y-1">
          <div className="flex flex-row justify-between">
            <p className="text-sm font-medium leading-[120%] text-base-text">
              {option.name}
            </p>
            {selectedPrices ? (
              <p className="text-xs font-bold text-base-text">
                {sumPricesDisplayString(selectedPrices)}
              </p>
            ) : (
              <p className="text-xs font-normal text-neutral-600">
                {getPricesDisplayString(option.variants.map((v) => v.price))}
              </p>
            )}
          </div>
          {option.description && (
            <p className="text-sm leading-snug text-neutral-600">
              {option.description}
            </p>
          )}
        </div>
        <div>
          <OptionInput
            option={option}
            field={field}
            variantOptions={variantOptions}
          />

          <InputError
            error={
              form.formState.errors.participants?.[participantIndex]
                ?.selectedOptions?.[option.optionId]?.selections?.message
            }
          />
          {quoteErrors && quoteErrors.length > 0 && (
            <div className="flex flex-col">
              {quoteErrors.map((error) => (
                <InputError key={error} error={error} />
              ))}
            </div>
          )}
        </div>

        {option.isFull && (
          <div className="text-sm text-amber-600">
            Ta opcja jest obecnie niedostępna (brak miejsc)
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const OptionInput = ({
  option,
  field,
  variantOptions,
}: {
  option: BookingOptionDto;
  field: ControllerRenderProps<
    ClientInputFormWithCustomFields,
    `participants.${number}.selectedOptions.${string}.selections`
  >;
  variantOptions: Option[];
}) => {
  if (option.isFull) {
    return <Chip label="Brak miejsc" tone="error" fill="soft" />;
  }

  if (variantOptions.length === 0) {
    return (
      <div className="text-sm text-neutral-600">
        Brak dostępnych wariantów dla Twojego wieku
      </div>
    );
  }

  if (option.allowOptionMultiSelect) {
    return (
      <PrimitiveMultiSelect
        options={variantOptions}
        value={field.value}
        onChange={field.onChange}
        placeholder="Wybierz opcję..."
        valueObjectComparator={(
          o: BookingSelectedOptionDto,
          v: BookingSelectedOptionDto,
        ) => {
          return o.variantId === v.variantId && o.priceId === v.priceId;
        }}
      />
    );
  } else {
    return (
      <PrimitiveSingleSelect
        options={variantOptions}
        value={field.value?.[0] ?? undefined}
        onChange={(v) => field.onChange(v ? [v] : [])}
        placeholder="Wybierz opcję..."
        isClearable={!option.required}
        valueObjectComparator={(
          o: BookingSelectedOptionDto,
          v: BookingSelectedOptionDto,
        ) => {
          return o.variantId === v.variantId && o.priceId === v.priceId;
        }}
      />
    );
  }
};

export default TourOptions;
